// ============================================
// LAYOUT COMPONENTS
// ============================================

import Sidebar from "./components/common/Sidebar";
import Navbar from "./components/common/Navbar";


// ============================================
// REPORTS
// ============================================

import Reports from "./pages/Reports";
import UserTimesheetReport from "./pages/reports/UserTimesheetReport";
import DailyEODReport from "./pages/reports/DailyEODReport";
import EffortSummaryReport from "./pages/reports/EffortSummaryReport";
import TimesheetReport from "./pages/reports/TimesheetReport";
import ProjectEffortReport from "./pages/reports/ProjectEffortReport";
import ApprovalLogReport from "./pages/reports/ApprovalLogReport";
import ProjectSummaryReport from "./pages/reports/ProjectSummaryReport";


// ============================================
// CORE PAGES
// ============================================

import Dashboard from "./pages/Dashboard";
import Projects from "./pages/Projects";
import WorkItemDetail from "./pages/workitems/WorkItemDetail";
import ProjectDetailsPage from "./pages/projects/ProjectDetailsPage";
import People from "./pages/People";
import Login from "./pages/Login";
import GanttView from "./pages/GanttView";

// ============================================
// LEAVE MANAGEMENT PAGES
// ============================================

import LeaveApply from "./pages/leaves/LeaveApply";
import LeaveStatement from "./pages/leaves/LeaveStatement";
import LeaveApprovals from "./pages/leaves/LeaveApprovals";
import LeaveMaintenance from "./pages/leaves/LeaveMaintenance";
import HolidayCalendar from "./pages/leaves/HolidayCalendar";
import LeaveOverview from "./pages/leaves/LeaveOverview";


// ============================================
// BILLING PAGES
// ============================================

import BillingOverview from "./pages/billing/BillingOverview";
import Clients from "./pages/billing/Clients";
import Invoices from "./pages/billing/Invoices";
import EffortSummary from "./pages/billing/EffortSummary";
import ProjectBilling from "./pages/billing/ProjectBilling";
import RateMaintenance from "./pages/billing/RateMaintenance";
import DraftInvoices from "./pages/billing/DraftInvoices"; // ⭐ NEW FEATURE: Draft invoice management page


// ============================================
// ACCOUNTING PAGES
// ============================================

import AccountingDashboard from "./pages/accounting/AccountingDashboard";
import Receivables from "./pages/accounting/Receivables";
import Payables from "./pages/accounting/Payables";
import Expenses from "./pages/accounting/Expenses";
import Payments from "./pages/accounting/Payments";
import AccountingReports from "./pages/accounting/Reports"; // ⭐ CHANGED: renamed import to avoid conflict with main Reports
import ChartOfAccounts from "./pages/accounting/ChartOfAccounts";


// ============================================
// AUTH PROTECTION
// ============================================

import ProtectedRoute from "./routes/ProtectedRoute";


// ============================================
// LIBRARIES
// ============================================

import { Box } from "@mui/material";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";


function App() {

  const location = useLocation();
  const isLoginPage = location.pathname === "/login";

  return (

    <Box sx={{ display: "flex" }}>

      {/* =========================
         SIDEBAR (hidden on login)
      ========================= */}

      {!isLoginPage && <Sidebar />}


      <Box sx={{ flexGrow: 1, ml: isLoginPage ? 0 : "240px" }}>

        {/* =========================
            NAVBAR (hidden on login)
        ========================= */}

        {!isLoginPage && <Navbar />}


        <Box sx={{ p: isLoginPage ? 0 : 3 }}>

          <Routes>

            {/* ====================================
                PUBLIC ROUTES
            ==================================== */}

            <Route path="/login" element={<Login />} />


            {/* ====================================
                REPORTS
            ==================================== */}

            <Route path="/reports" element={<Reports />} />

            <Route path="/reports/daily-eod" element={<DailyEODReport />} />
            <Route path="/reports/effort-summary" element={<EffortSummaryReport />} />
            <Route path="/reports/timesheet" element={<TimesheetReport />} />
            <Route path="/reports/project-effort" element={<ProjectEffortReport />} />
            <Route path="/reports/approval-log" element={<ApprovalLogReport />} />
            <Route path="/reports/project-summary" element={<ProjectSummaryReport />} />
			<Route path="/projects/:id/gantt" element={<GanttView />} />

            {/* ====================================
                BILLING MODULE
            ==================================== */}

            {/* Redirect billing root */}
            <Route
              path="/billing"
              element={<Navigate to="/billing/overview" />}
            />

            <Route
              path="/billing/overview"
              element={
                <ProtectedRoute>
                  <BillingOverview />
                </ProtectedRoute>
              }
            />

            <Route
              path="/billing/rate-maintenance"
              element={
                <ProtectedRoute>
                  <RateMaintenance />
                </ProtectedRoute>
              }
            />

            <Route
              path="/billing/clients"
              element={
                <ProtectedRoute>
                  <Clients />
                </ProtectedRoute>
              }
            />

            <Route
              path="/billing/effort-summary"
              element={
                <ProtectedRoute>
                  <EffortSummary />
                </ProtectedRoute>
              }
            />

            <Route
              path="/billing/project-billing"
              element={
                <ProtectedRoute>
                  <ProjectBilling />
                </ProtectedRoute>
              }
            />

            <Route
              path="/billing/invoices"
              element={
                <ProtectedRoute>
                  <Invoices />
                </ProtectedRoute>
              }
            />

            {/* ⭐ Draft invoice management */}
            <Route
              path="/billing/drafts"
              element={
                <ProtectedRoute>
                  <DraftInvoices />
                </ProtectedRoute>
              }
            />


            {/* ====================================
                ACCOUNTING MODULE
            ==================================== */}

            {/* ⭐ ADDED: Redirect accounting root */}
            <Route
              path="/accounting"
              element={<Navigate to="/accounting/dashboard" />} // ⭐ NEW
            />

            {/* ⭐ ADDED: Accounting Dashboard */}
            <Route
              path="/accounting/dashboard"
              element={
                <ProtectedRoute>
                  <AccountingDashboard />
                </ProtectedRoute>
              }
            />

            {/* ⭐ ADDED: Receivables */}
            <Route
              path="/accounting/receivables"
              element={
                <ProtectedRoute>
                  <Receivables />
                </ProtectedRoute>
              }
            />

            {/* ⭐ ADDED: Payables */}
            <Route
              path="/accounting/payables"
              element={
                <ProtectedRoute>
                  <Payables />
                </ProtectedRoute>
              }
            />

            {/* ⭐ ADDED: Expenses */}
            <Route
              path="/accounting/expenses"
              element={
                <ProtectedRoute>
                  <Expenses />
                </ProtectedRoute>
              }
            />

            {/* ⭐ ADDED: Payments */}
            <Route
              path="/accounting/payments"
              element={
                <ProtectedRoute>
                  <Payments />
                </ProtectedRoute>
              }
            />

            {/* ⭐ ADDED: Chart of Accounts */}
            <Route
              path="/accounting/chart-of-accounts"
              element={
                <ProtectedRoute>
                  <ChartOfAccounts />
                </ProtectedRoute>
              }
            />

            {/* ⭐ ADDED: Accounting Reports */}
            <Route
              path="/accounting/reports"
              element={
                <ProtectedRoute>
                  <AccountingReports />
                </ProtectedRoute>
              }
            />


            {/* ====================================
                LEAVE MANAGEMENT
            ==================================== */}

            <Route
              path="/leaves"
              element={<Navigate to="/leaves/overview" />}
            />

            <Route
              path="/leaves/overview"
              element={
                <ProtectedRoute>
                  <LeaveOverview />
                </ProtectedRoute>
              }
            />

            <Route
              path="/leaves/apply"
              element={
                <ProtectedRoute>
                  <LeaveApply />
                </ProtectedRoute>
              }
            />

            <Route
              path="/leaves/statement"
              element={
                <ProtectedRoute>
                  <LeaveStatement />
                </ProtectedRoute>
              }
            />

            <Route
              path="/leaves/approvals"
              element={
                <ProtectedRoute>
                  <LeaveApprovals />
                </ProtectedRoute>
              }
            />

            <Route
              path="/leaves/maintenance"
              element={
                <ProtectedRoute>
                  <LeaveMaintenance />
                </ProtectedRoute>
              }
            />

            <Route
              path="/leaves/holidays"
              element={
                <ProtectedRoute>
                  <HolidayCalendar />
                </ProtectedRoute>
              }
            />


            {/* ====================================
                WORK ITEM
            ==================================== */}

            <Route
              path="/work-items/:id"
              element={
                <ProtectedRoute>
                  <WorkItemDetail />
                </ProtectedRoute>
              }
            />


            {/* ====================================
                MAIN APPLICATION
            ==================================== */}

            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              }
            />

            <Route
              path="/projects"
              element={
                <ProtectedRoute>
                  <Projects />
                </ProtectedRoute>
              }
            />

            <Route
              path="/projects/:id"
              element={
                <ProtectedRoute>
                  <ProjectDetailsPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/people"
              element={
                <ProtectedRoute>
                  <People />
                </ProtectedRoute>
              }
            />


            {/* ====================================
                DEFAULT FALLBACK
            ==================================== */}

            <Route path="*" element={<Navigate to="/" />} />

          </Routes>

        </Box>

      </Box>

    </Box>

  );
}

export default App;